import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Divider, IconButton, Paper } from '@mui/material';
import { ArrowIcon, LeftRightExpandedIcon, LeftRightIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconOrientation } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoading from 'hooks/useLoading';

import { getEmailBodyAction, readEmailsAction } from 'store/loanApplications/actions';

import { ActivitiesTestIds } from '../../../Activities.testIds';

import EmailHeader from './EmailHeader';
import SingleEmail from './SingleEmail';

const EmailActivityItem = ({ activity, applicationId }) => {
  const dispatch = useDispatch();

  const isLightWeightEmailBodyAllowed = useFeatureToggle(FeatureFlag.EnableLightWeightEmailBody);

  const { emails: unsortedEmails } = activity;

  const isSingleEmail = unsortedEmails.length === 1;

  const [newEmails, setNewEmails] = useState(unsortedEmails.filter(({ isNewEmail }) => isNewEmail));
  const [isBodyLoading, setIsBodyLoading] = useLoading(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [expandedCollapsedEmailsState, setExpandedCollapsedEmailsState] = useState({});

  const emails = useMemo(
    () => _.orderBy(unsortedEmails, 'activityDateTime', 'desc'),
    [unsortedEmails],
  );

  const initialEmailsStateOnExpand = useMemo(
    () =>
      emails.reduce((acc, email, index) => {
        const shouldEmailBeExpanded = !index || email.isNewEmail;

        if (shouldEmailBeExpanded) {
          acc[email.id] = shouldEmailBeExpanded;
        }

        return acc;
      }, {}),
    [emails],
  );

  const isAllEmailsExpanded = useMemo(
    () => emails.every(({ id }) => expandedCollapsedEmailsState[id]),
    [emails, expandedCollapsedEmailsState],
  );

  const allEmailsMadeExpanded = useMemo(
    () =>
      emails.reduce((acc, email) => {
        acc[email.id] = true;

        return acc;
      }, {}),
    [emails],
  );

  const onEmailThreadClick = useCallback(() => {
    const isReadingEmailsAllowed =
      isCollapsed && newEmails.length && !isLightWeightEmailBodyAllowed;

    setExpandedCollapsedEmailsState(initialEmailsStateOnExpand);

    if (isReadingEmailsAllowed) {
      dispatch(readEmailsAction(newEmails.map(({ id }) => id))).catch(Error);
    }

    if (!isCollapsed) {
      setNewEmails([]);
    }

    setIsCollapsed(!isCollapsed);
  }, [
    setNewEmails,
    isCollapsed,
    initialEmailsStateOnExpand,
    dispatch,
    newEmails,
    isLightWeightEmailBodyAllowed,
  ]);

  const onToggleEmail = useCallback(
    emailId =>
      setExpandedCollapsedEmailsState(prevState => ({
        ...prevState,
        [emailId]: !prevState[emailId],
      })),
    [],
  );

  const onExpandCollapseToggle = useCallback(
    e => {
      const isAllEmailItemsExpanded = e.currentTarget.dataset.isAllEmailsExpanded;

      if (isAllEmailItemsExpanded === 'true') {
        setExpandedCollapsedEmailsState({});

        return;
      }

      setExpandedCollapsedEmailsState(allEmailsMadeExpanded);
    },
    [allEmailsMadeExpanded],
  );

  useEffectSafeStrictMode(() => {
    if (!isLightWeightEmailBodyAllowed) {
      const emailsWithoutBody = emails.filter(({ body }) => !body);

      if (emailsWithoutBody.length && !isBodyLoading) {
        setIsBodyLoading(true);

        Promise.all(emailsWithoutBody.map(({ id }) => dispatch(getEmailBodyAction(id))))
          .catch(Error)
          .finally(() => setIsBodyLoading(false));
      }
    }
  }, [
    dispatch,
    emails,
    isBodyLoading,
    setIsBodyLoading,
    isCollapsed,
    isSingleEmail,
    isLightWeightEmailBodyAllowed,
  ]);

  if (isSingleEmail) {
    return (
      <Paper className="flex w-full rounded-12 mb-8">
        <SingleEmail
          applicationId={applicationId}
          emailId={emails[0].id}
          isBodyLoading={isBodyLoading}
          isExpanded={expandedCollapsedEmailsState[emails[0].id]}
          onEmailToggle={onToggleEmail}
        />
      </Paper>
    );
  }

  return (
    <Paper className="flex w-full rounded-12 mb-8">
      {isCollapsed && (
        <EmailHeader
          emailId={emails[0].id}
          emailsCount={emails.length}
          isBodyLoading={isBodyLoading}
          isNew={emails[0].isNewEmail}
          onEmailThreadClick={onEmailThreadClick}
        />
      )}
      {!isCollapsed && (
        <>
          <div className="flex flex-col items-center">
            <IconButton
              color="inherit"
              data-testid={ActivitiesTestIds.EMAIL_THREAD_BACK_BUTTON}
              onClick={onEmailThreadClick}
            >
              <ArrowIcon direction={IconDirection.LEFT} />
            </IconButton>

            <ButtonWithTooltip
              data-is-all-emails-expanded={isAllEmailsExpanded}
              data-testid={ActivitiesTestIds.EMAIL_ACTIVITY_ITEM_UNFOLD_ICON_BUTTON}
              icon={isAllEmailsExpanded ? LeftRightExpandedIcon : LeftRightIcon}
              iconOrientation={IconOrientation.VERTICAL}
              spanContainerClassName="mt-16"
              title={isAllEmailsExpanded ? 'Collapse all' : 'Expand all'}
              onClick={onExpandCollapseToggle}
            />
          </div>
          <div className="border-l-2 flex-auto w-full">
            {emails.map(({ id }, index) => (
              <Fragment key={id}>
                {!!index && <Divider className="h-1" />}
                <SingleEmail
                  applicationId={applicationId}
                  emailId={id}
                  isBodyLoading={isBodyLoading}
                  isExpanded={expandedCollapsedEmailsState[id]}
                  onEmailToggle={onToggleEmail}
                />
              </Fragment>
            ))}
          </div>
        </>
      )}
    </Paper>
  );
};

EmailActivityItem.propTypes = {
  activity: PropTypes.shape({
    type: PropTypes.string,
    activityDateTime: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.string,
    body: PropTypes.string,
  }).isRequired,
  applicationId: PropTypes.string.isRequired,
};

export default memo(EmailActivityItem);
